import {CoreLogger, debug} from '@wix/document-manager-core'
import {
    CreateTransactionRequest,
    CreateTransactionResponse,
    DuplexerChannel,
    GetDocumentRequest,
    GetDocumentResponse,
    GetTransactionResponse,
    csaveDuplexer
} from '@wix/document-manager-extensions'
import type {
    ContinuousSaveServer,
    SaveTransactionResponse
} from '@wix/document-manager-extensions/src/extensions/csave/csaveTypes'
import type {ViewsRESTRes, ViewsServer} from '@wix/document-manager-extensions/src/extensions/views/viewsTypes'
import type {
    CreateRevisionRequest,
    ServerCreateRevisionResponse,
    GetTransactionsResponse,
    SaveRequest
} from '@wix/document-manager-extensions/src/types'
import type {
    DistributePayload,
    FragmentTypesFilter
} from '@wix/ambassador-document-management-document-store-v1-transaction/types'
import {deleteJson, getJsonWithParams, postJsonWithAuth, Query} from '../utils/fetch'
import {HttpClient} from '@wix/http-client'
import {getSitePagesNamespaces} from '@wix/ambassador-document-management-pages-metadata-manager-v1-page-metadata/http' //eslint-disable-line wix-editor/no-internal-import
import {mapTransactionResponse, mapTransactionsResponse} from './dtos'

const log = debug('csave')

export const MAX_LONG = '9223372036854775807'

export const getFragmentTypesFilter = (fragmentTypesFilter?: FragmentTypesFilter) => {
    return fragmentTypesFilter?.allTypes ? {'fragmentTypesFilter.allTypes': true} : {}
}

interface Urls {
    createRevision: string
    save: string
    save2: string
    transactions: string
    document: string
    views: string
    distributor: string
    fragments: string
    oldModifySiteApps: string
    modifySiteApps: string
    schemaCollections: string
}

/**
 * based on
 * https://github.com/wix-private/editor-server/blob/master/wix-html-server/editor-document-store/src/main/proto/com/wixpress/editor/api/document_store.proto
 */
export class ServerFacade implements ContinuousSaveServer, ViewsServer {
    protected lastTransactionId: string | undefined
    protected lastUpdatedOnServer: string | undefined
    protected lastSaveDate: Date | undefined

    private instanceProvider: () => string = () => {
        throw new Error('Instance provider was called before `setInstanceProvider` was called')
    }

    private httpClient = new HttpClient({getAppToken: this.instanceProvider})
    protected urls: Urls = {
        createRevision: '',
        save: '',
        save2: '',
        transactions: '',
        document: '',
        views: '',
        distributor: '',
        fragments: '',
        modifySiteApps: '',
        schemaCollections: '',
        oldModifySiteApps: ''
    }

    constructor(editorRootUrl: string) {
        this.initUrls(editorRootUrl)
    }

    initUrls(editorRootUrl: string) {
        const docStore = `${editorRootUrl}_api/editor-document-store/`
        const editorFragments = `${editorRootUrl}_api/editor-fragments/`
        const baseUrlDocStore = `${docStore}v1/`
        const baseUrl2DocStore = `${docStore}v2/`
        const baseUrlFragments = `${editorFragments}v1/`
        this.urls.createRevision = `${editorRootUrl}html/v1/create_revision`
        this.urls.save = `${baseUrlDocStore}save`
        this.urls.save2 = `${baseUrl2DocStore}save`
        this.urls.transactions = `${baseUrlDocStore}transactions`
        this.urls.views = `${editorRootUrl}_api/editor-pages-metadata/v1/sitePagesNamespaces`
        this.urls.distributor = `${baseUrlDocStore}distribute`
        this.urls.document = `${baseUrlDocStore}document`
        this.urls.fragments = `${baseUrlFragments}fragments`
        this.urls.oldModifySiteApps = `${editorRootUrl}html/v1/modifySiteApps`
        this.urls.modifySiteApps = `${editorRootUrl}html/v2/modifySiteApps`
        this.urls.schemaCollections = `${editorRootUrl}_api/schema-collection/v1/schema-collections`
    }

    setInstanceProvider(provider: () => string): void {
        this.instanceProvider = provider
    }

    async deleteTransactions(instance: string, to: string = MAX_LONG): Promise<void> {
        // deleteTransaction must include body param to => the transactions up until this ID will be deleted
        const payload = {to}
        const result = await deleteJson(this.urls.transactions, instance, payload)
        this.lastTransactionId = result.transactionId
        log.info('deleteTransactions done', result)
    }

    async createRevision(req: CreateRevisionRequest): Promise<ServerCreateRevisionResponse> {
        log.info('createRevision')
        return await postJsonWithAuth(this.urls.createRevision, this.instanceProvider(), req)
    }

    async getTransactions(
        afterTransactionId: string,
        untilTransactionId: string,
        branchId: string,
        fragmentTypesFilter?: FragmentTypesFilter
    ): Promise<GetTransactionsResponse> {
        const fragmentQuery = getFragmentTypesFilter(fragmentTypesFilter)
        const queryParams = {afterTransactionId, untilTransactionId, branchId, ...fragmentQuery}
        const res = await getJsonWithParams(this.urls.transactions, queryParams, this.instanceProvider())
        return fragmentTypesFilter?.allTypes ? mapTransactionsResponse(res) : res
    }

    async getJsonWithParams(url: string, params: Record<string, any>) {
        return await getJsonWithParams(url, params, this.instanceProvider())
    }

    async distributeMessage(payloads: DistributePayload[]): Promise<void> {
        return await postJsonWithAuth(this.urls.distributor, this.instanceProvider(), {payloads})
    }

    async getViews(): Promise<ViewsRESTRes> {
        const httpResponse = await this.httpClient.request(getSitePagesNamespaces({}))
        return httpResponse.data as ViewsRESTRes
    }

    async getTransaction(
        transactionId: string,
        branchId?: string,
        fragmentTypesFilter?: FragmentTypesFilter
    ): Promise<GetTransactionResponse> {
        const fragmentQuery = getFragmentTypesFilter(fragmentTypesFilter)
        const queryParams = {branchId, ...fragmentQuery}
        const res = await getJsonWithParams(
            `${this.urls.transactions}/${transactionId}`,
            queryParams ?? {},
            this.instanceProvider()
        )
        return fragmentTypesFilter?.allTypes ? mapTransactionResponse(res) : res
    }

    async getStore(
        branchId: string,
        afterTransactionId: string,
        untilTransactionId?: string,
        fragmentTypesFilter?: FragmentTypesFilter
    ): Promise<GetDocumentResponse> {
        const fragmentQuery = getFragmentTypesFilter(fragmentTypesFilter)
        const params: GetDocumentRequest = {afterTransactionId, untilTransactionId, branchId, ...fragmentQuery}
        return getJsonWithParams(this.urls.document, params as Query, this.instanceProvider())
    }

    /**
     * first createTransaction should be sent without lastTransactionId, the subsequent calls must include the previous tx id as lastTransactionId
     * PendingServerTransaction
     */
    async save(payload: CreateTransactionRequest): Promise<CreateTransactionResponse> {
        log.info('sending tx')
        return await postJsonWithAuth<CreateTransactionResponse>(this.urls.save, this.instanceProvider(), payload)
    }

    async asyncSave(payload: SaveRequest): Promise<void> {
        log.info('sending pending tx')
        return await postJsonWithAuth<void>(this.urls.save2, this.instanceProvider(), payload)
    }

    setLast(transactionId: string) {
        this.lastTransactionId = transactionId
        this.setLastUpdatedOnServer(transactionId)
    }

    getLast(): string | undefined {
        return this.lastTransactionId
    }
    setLastUpdatedOnServer(transactionId: string) {
        this.lastUpdatedOnServer = transactionId
    }

    getLastUpdatedOnServer(): string | undefined {
        return this.lastUpdatedOnServer
    }

    setLastSaveDate(date: Date) {
        this.lastSaveDate = date
    }

    getLastSaveDate(): Date | undefined {
        return this.lastSaveDate
    }

    createDuplexer(
        wixInstanceProvider: csaveDuplexer.WixInstanceProvider,
        origin: string,
        logger: CoreLogger,
        branch?: string
    ) {
        return new DuplexerChannel(wixInstanceProvider, origin, logger, branch)
    }

    async onChannelReady(): Promise<void> {}

    async saveSyncWithMultipleTransactions(): Promise<SaveTransactionResponse> {
        throw new Error('not implemented')
    }
}
